import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { ThemeState } from '@/types/store';
import logger from '@/services/logger';
import { darkTheme, lightTheme, typography, animation } from '@/styles/themes';

// Omit the mode and other non-color properties for the theme color values
type ThemeColors = Omit<ThemeState, 'mode' | 'colorScheme' | 'systemPreference'>;

// Interface for the context value
interface ThemeContextValue extends ThemeColors {
  mode: 'light' | 'dark';
  colorScheme: 'default' | 'blue' | 'green' | 'purple' | 'cyberpunk' | 'minimal';
  systemPreference: boolean;
  isDarkMode: boolean;
  setMode: (mode: 'light' | 'dark') => void;
  toggleMode: () => void;
  setColorScheme: (scheme: string) => void;
  useSystemPreference: () => void;
  getCssVar: (name: string) => string;
}

// Create the theme context with a default value
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const theme = useTheme();

  // Enhanced function to apply theme to root element
  const applyThemeToRoot = () => {
    const root = document.documentElement;
    const themeValues = theme.mode === 'dark' ? darkTheme : lightTheme;

    // Apply color values
    Object.entries(themeValues).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });

    // Apply typography values
    Object.entries(typography.fontFamily).forEach(([key, value]) => {
      root.style.setProperty(`--font-${key}`, value);
    });

    // Apply animation values
    Object.entries(animation.duration).forEach(([key, value]) => {
      root.style.setProperty(`--animation-duration-${key}`, value);
    });

    Object.entries(animation.timing).forEach(([key, value]) => {
      root.style.setProperty(`--animation-timing-${key}`, value);
    });

    // Add/remove dark class based on mode
    if (theme.mode === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    logger.debug('Enhanced theme applied to document', {
      module: 'theme',
      data: { mode: theme.mode, colorScheme: theme.colorScheme },
    });
  };

  // Apply theme on initial render and when values change
  useEffect(() => {
    applyThemeToRoot();
  }, [theme.mode, theme.colorScheme]);

  // Listen for system preference changes if using system preference
  useEffect(() => {
    if (!theme.systemPreference) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      logger.debug('System color scheme preference changed', {
        module: 'theme',
      });

      if (theme.systemPreference) {
        if (e.matches && theme.mode !== 'dark') {
          theme.setMode('dark');
        } else if (!e.matches && theme.mode !== 'light') {
          theme.setMode('light');
        }
      }
    };

    // Add event listener for changes
    mediaQuery.addEventListener('change', handleChange);

    // Clean up
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme.systemPreference]);

  const contextValue: ThemeContextValue = {
    ...(theme as unknown as ThemeContextValue),
  };

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

// Custom hook to use the theme context
export const useThemeContext = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }

  return context;
};
